import { AppRouter } from "./components";
import "./index.css";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { initScreenWidth } from "./utils/screenWidth";
import { useFavoriteGames } from "./hooks/useFavoriteGames";
import { useIOSKeyboardFix } from "./hooks/useIOSKeyboardFix";
import { useUserDataFlattened } from "@/modules/user/stores";
import { PopupQueueProvider } from "./contexts/PopupQueueContext";
import { FloatingIconsProvider } from "./contexts/FloatingIconsContext";
import { useCustomerServices } from "./modules/homeDisplay/hooks";
import { useGlobalValue } from "./stores/GlobalState";
import BlockedDevicePage from "./pages/BlockedDevice";
import "./utils/imageReloadManager";

// LCP/LT 优化：把非首屏关键的弹窗 / 客服 / Push 等组件延后到主线程空闲后
// 再挂载，避免它们的 lazy chunk 在 App mount 时立即触发下载 + 评估，
// 挤占 LCP 关键资源。
type IdleRequestCallback = (deadline: { didTimeout: boolean }) => void;
type RequestIdleCallback = (
  cb: IdleRequestCallback,
  opts?: { timeout: number }
) => number;
type CancelIdleCallback = (handle: number) => void;

function useIdleReady(timeoutMs = 3000): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return;

    let cancelled = false;
    let idleHandle: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const trigger = () => {
      if (cancelled) return;
      cancelled = true;
      setReady(true);
    };

    const ric = (
      window as unknown as { requestIdleCallback?: RequestIdleCallback }
    ).requestIdleCallback;
    const cic = (
      window as unknown as { cancelIdleCallback?: CancelIdleCallback }
    ).cancelIdleCallback;

    if (typeof ric === "function") {
      idleHandle = ric(trigger, { timeout: timeoutMs });
    }
    timeoutId = setTimeout(trigger, timeoutMs);

    return () => {
      cancelled = true;
      if (idleHandle !== undefined && typeof cic === "function") {
        cic(idleHandle);
      }
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [ready, timeoutMs]);

  return ready;
}

const LeftBottomFloatingIcons = lazy(() =>
  import("./components/LeftBottomFloatingIcons").then(m => ({
    default: m.LeftBottomFloatingIcons,
  }))
);
const TawkToChat = lazy(() =>
  import("./components/TawkToChat").then(m => ({ default: m.TawkToChat }))
);
const LiveChat = lazy(() =>
  import("./components/LiveChat").then(m => ({ default: m.LiveChat }))
);
const GlobalSafariAddHomeScreen = lazy(() =>
  import("./pages/LandingPage/components/SafariAddHomeScreen").then(m => ({
    default: m.GlobalSafariAddHomeScreen,
  }))
);
const GlobalDownloadAppPopup = lazy(() =>
  import("./components/DownloadAppPopup/GlobalDownloadAppPopup").then(m => ({
    default: m.GlobalDownloadAppPopup,
  }))
);
const GlobalAppDownloadDialog = lazy(() =>
  import("./components/AppDownloadDialog").then(m => ({
    default: m.GlobalAppDownloadDialog,
  }))
);
const WebPush = lazy(() => import("./components/WebPush"));
const GlobalEvent = lazy(() =>
  import("./components/Event").then(m => ({ default: m.GlobalEvent }))
);
const GlobalRulesDialog = lazy(() =>
  import("./components/RulesDialog").then(m => ({
    default: m.GlobalRulesDialog,
  }))
);
const PrerequisitePopups = lazy(() =>
  import("./components/PrerequisitePopups/PrerequisitePopups").then(m => ({
    default: m.PrerequisitePopups,
  }))
);
// AgreementConfirmation 与 UnpaidGuideController 都不在首屏关键路径
const AgreementConfirmation = lazy(() =>
  import("./components/AgreementConfirmation").then(m => ({
    default: m.AgreementConfirmation,
  }))
);
const UnpaidGuideController = lazy(() =>
  import("./components/UnpaidGuideModal").then(m => ({
    default: m.UnpaidGuideController,
  }))
);
function App() {
  useFavoriteGames();
  useIOSKeyboardFix();

  const [deviceBlacklisted] = useGlobalValue("deviceBlacklisted");
  const { isAuthenticated, userId, username, email } = useUserDataFlattened();
  const customerServices = useCustomerServices();
  // LCP 之后再挂载非关键弹窗：requestIdleCallback 优先，3s 兜底
  const idleReady = useIdleReady();

  // 获取在线客服配置（必须在条件 return 之前调用，保证 Hooks 数量一致，避免 React #300）
  const onlineServiceConfig = useMemo(() => {
    const onlineService = customerServices.find(
      service => service.customerCategory === "ONLINE_SERVICE"
    );
    if (!onlineService) return null;

    const serviceType = onlineService.serviceType;
    if (serviceType === "TAWK_TO") {
      // TAWK_TO 需要 propertyId 和 widgetId
      if (onlineService.propertyId && onlineService.widgetId) {
        return {
          type: "TAWK_TO" as const,
          propertyId: onlineService.propertyId,
          widgetId: onlineService.widgetId,
        };
      }
    } else if (serviceType === "LIVECHAT") {
      // LIVE_CHAT 需要 licenseId
      if (onlineService.licenseId) {
        return {
          type: "LIVE_CHAT" as const,
          licenseId: onlineService.licenseId,
        };
      }
    }
    return null;
  }, [customerServices]);

  useEffect(() => {
    document.body.setAttribute("data-device", "mobile");

    // 真实 iOS 设备标记（电脑浏览器模拟器不会命中）
    const isRealIOS =
      ["iPad", "iPhone", "iPod"].some(d => navigator.platform?.includes(d)) ||
      (navigator.userAgent.includes("Mac") && "ontouchend" in document);
    if (isRealIOS) {
      document.body.classList.add("is-ios");
    }

    const isRealAndroid =
      /Android/i.test(navigator.userAgent) && "ontouchend" in document;
    if (isRealAndroid) {
      document.body.classList.add("is-android");
    }

    initScreenWidth();

    return () => {
      document.body.removeAttribute("data-device");
      document.body.classList.remove("is-ios");
      document.body.classList.remove("is-android");
    };
  }, []);

  // 设备被拉入黑名单时，仅展示拦截页，不展示当前页面信息（放在所有 Hooks 之后，避免 React #300）
  if (deviceBlacklisted) {
    return <BlockedDevicePage />;
  }

  // 应用主界面
  return (
    <div className="phone-wrapper">
      <AppRouter />
      {/* 客服 / 安装提示 / 规则弹窗：客服 SDK 体积较大且非首屏关键，等 idle 再挂载 */}
      {idleReady && (
        <Suspense fallback={null}>
          {onlineServiceConfig?.type === "TAWK_TO" && (
            <TawkToChat
              propertyId={onlineServiceConfig.propertyId}
              widgetId={onlineServiceConfig.widgetId}
              hideWidget={true}
              user={
                isAuthenticated
                  ? {
                      userId: userId?.toString(),
                      name: username,
                      email: email,
                    }
                  : undefined
              }
              onLoad={() => console.log("Tawk.to loaded globally")}
            />
          )}
          {onlineServiceConfig?.type === "LIVE_CHAT" && (
            <LiveChat
              license={onlineServiceConfig.licenseId}
              hideWidget={true}
              user={
                isAuthenticated
                  ? {
                      userId: userId?.toString(),
                      name: username,
                      email: email,
                    }
                  : undefined
              }
              onLoad={() => console.log("LiveChat loaded globally")}
            />
          )}
          <GlobalSafariAddHomeScreen />
          <GlobalRulesDialog />
          {/* AgreementConfirmation：事件模式（监听 show-agreement-confirmation
              事件），由 Login/Recharge 用户操作触发，首屏 1-3s 内不可能完成登录
              动作，监听器晚注册零业务影响 */}
          <AgreementConfirmation />
        </Suspense>
      )}
      <Suspense fallback={null}>
        {/* PopupQueueProvider 和 FloatingIconsProvider 立即挂载：
            LeftBottomFloatingIcons 依赖 FloatingIconsContext 的真实上下文，
            否则会走 useFloatingIconsContext 的空 fallback，丢失首屏悬浮图标。
            两个 Provider 自身比较轻；真正重的 PrerequisitePopups / WebPush /
            Global* / GlobalEvent 等弹窗组件 lazy chunk 由 idleReady 网关
            延迟到主线程空闲后再触发下载与挂载。 */}
        <PopupQueueProvider initialPaused>
          <FloatingIconsProvider>
            <LeftBottomFloatingIcons />
            {idleReady && (
              <>
                <PrerequisitePopups />
                <WebPush />
                <GlobalDownloadAppPopup />
                <GlobalAppDownloadDialog />
                <GlobalEvent
                // firstRechargeVisible={false}
                // cashbackVisible={false}
                // rechargeOfferVisible={false}
                // redPacketVisible={false}
                // rescueFundVisible={false}
                />
              </>
            )}
          </FloatingIconsProvider>
        </PopupQueueProvider>
      </Suspense>
      {/* UnpaidGuideController：shouldRun = isAuthenticated && enableLastPaymentVoucher，
          且首次 mount 不会立即弹（hasCheckedNewRef 双重保护），
          只对挂载后新增的订单弹窗——首屏 1-3s 内不会有"新增订单"，可安全延迟 */}
      {idleReady && (
        <Suspense fallback={null}>
          <UnpaidGuideController />
        </Suspense>
      )}
    </div>
  );
}

export default App;
